import { IndicatorType } from '@shared/types';
import { IChartApi, ISeriesApi, LineSeries } from 'lightweight-charts';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useChartContext } from '../components/chart.context';
import { useChartStreamContext } from '../components/chart-stream.context';
import { CenteredLineLabel } from '../primitives/line-label.primitive';
import {
  toNumericTimeOrNull,
  toSortableTime
} from './utils/time-normalization.utils';
import { useIndicatorUpdateStream } from './use-indicator-update-stream.hook';
import {
  isMarketStructureIndicatorUpdate,
  MarketStructureIndicatorUpdate,
  MarketStructureIndicatorUpdatePoint
} from '../services/indicator-update.service';
import { timeToTimestamp } from '../services/timeframe-utils';

const showZigZag = false;

type MarketStructureLineSeriesMap = Map<number, ISeriesApi<'Line'>[]>;
type MarketStructureZigZagSeriesMap = Map<number, ISeriesApi<'Line'>>;
type MarketStructurePointCache = Map<
  string,
  MarketStructureIndicatorUpdatePoint
>;

// Do not key by `point.time`: multiple structure events can share candle time,
// which would overwrite older BOS/CHOCH lines and leave only the latest one.
const keyForStructurePoint = (point: MarketStructureIndicatorUpdatePoint) => {
  const pivotTime = toNumericTimeOrNull(point.lastPivotTime);
  if (pivotTime != null) return `p:${pivotTime}`;

  const breakTime = toNumericTimeOrNull(point.breakTime);
  if (breakTime != null) {
    return `b:${breakTime}:${point.type ?? '?'}:${point.direction ?? '?'}`;
  }

  return `t:${toSortableTime(point.time)}:${point.type ?? '?'}:${point.direction ?? '?'}`;
};

const sortValueForStructurePoint = (
  point: MarketStructureIndicatorUpdatePoint
) => {
  const pivotTime = toNumericTimeOrNull(point.lastPivotTime);
  if (pivotTime != null) return pivotTime;

  return toSortableTime(point.time);
};

export const useMarketStructure = () => {
  const {
    chartRef,
    chartInitialized,
    data: { indicatorConfigs }
  } = useChartContext();
  const { timeframeIndex } = useChartStreamContext();

  // Rendered objects are cached per indicatorIndex so multiple MARKET_STRUCTURE
  // configs can be updated/cleared independently.
  const structureSeriesRefs = useRef<MarketStructureLineSeriesMap>(new Map());
  const zigZagSeriesRefs = useRef<MarketStructureZigZagSeriesMap>(new Map());
  // Cache latest merged payload because deltas are partial (single-point updates).
  const indicatorDataCacheRef = useRef<Map<number, MarketStructurePointCache>>(
    new Map()
  );

  const hasMarketStructure = useMemo(
    () =>
      indicatorConfigs.overlay.some(
        (indicator) => indicator.type === IndicatorType.MARKET_STRUCTURE
      ),
    [indicatorConfigs.overlay]
  );

  const clearStructureSeriesForIndicator = useCallback(
    (indicatorIndex: number) => {
      const seriesArray = structureSeriesRefs.current.get(indicatorIndex);
      if (!seriesArray?.length) return;

      seriesArray.forEach((series) => {
        chartRef.current?.removeSeries(series);
      });

      structureSeriesRefs.current.delete(indicatorIndex);
    },
    []
  );

  const clearZigZagSeriesForIndicator = useCallback(
    (indicatorIndex: number) => {
      const series = zigZagSeriesRefs.current.get(indicatorIndex);
      if (!series) return;

      chartRef.current?.removeSeries(series);
      zigZagSeriesRefs.current.delete(indicatorIndex);
    },
    []
  );

  const clearAll = useCallback(() => {
    Array.from(structureSeriesRefs.current.keys()).forEach(
      clearStructureSeriesForIndicator
    );
    Array.from(zigZagSeriesRefs.current.keys()).forEach(
      clearZigZagSeriesForIndicator
    );
  }, [clearStructureSeriesForIndicator, clearZigZagSeriesForIndicator]);

  const getLineColor = useCallback(
    (
      type: 'BOS' | 'CHOCH' | undefined,
      direction: 'BULLISH' | 'BEARISH' | undefined
    ) => {
      if (!type || !direction) return 'rgba(255, 255, 255, 0.5)';

      if (type === 'BOS') {
        return direction === 'BULLISH'
          ? 'rgba(76, 175, 80, 0.5)'
          : 'rgba(244, 67, 54, 0.5)';
      }

      return direction === 'BULLISH'
        ? 'rgba(33, 150, 243, 0.5)'
        : 'rgba(255, 152, 0, 0.5)';
    },
    []
  );

  const createStructureLineWithLabel = useCallback(
    (chart: IChartApi, item: MarketStructureIndicatorUpdatePoint) => {
      const startTime = timeToTimestamp(item.lastPivotTime);
      const endTime = timeToTimestamp(item.breakTime);
      // Draw at the broken pivot level (horizontal line), not at break candle extreme.
      const breakLevelPrice = item.lastPivotPrice ?? item.price;

      if (
        !item.type ||
        !item.direction ||
        startTime == null ||
        endTime == null ||
        typeof breakLevelPrice !== 'number'
      ) {
        return null;
      }

      const color = getLineColor(item.type, item.direction);
      const structureSeries = chart.addSeries(LineSeries, {
        color,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false
      });

      structureSeries.setData([
        {
          time: startTime,
          value: breakLevelPrice
        },
        {
          time: endTime,
          value: breakLevelPrice
        }
      ]);

      structureSeries.attachPrimitive(
        new CenteredLineLabel(chart, structureSeries, {
          text: item.type,
          textColor: color,
          backgroundColor: 'transparent',
          font: '12px Arial',
          padding: 4,
          verticalOffset: 12,
          borderRadius: 4,
          horizontalOffset: -50,
          placeBelowLine: item.direction === 'BEARISH',
          getPointAtLogical: () => ({
            time: endTime,
            price: breakLevelPrice
          })
        })
      );

      return structureSeries;
    },
    [getLineColor]
  );

  const mergeIndicatorData = useCallback(
    (
      indicatorIndex: number,
      points: MarketStructureIndicatorUpdate['data'],
      replace: boolean
    ) => {
      // Deltas are partial; merge into per-indicator cache before rebuilding visuals.
      const nextCache: MarketStructurePointCache = replace
        ? new Map()
        : new Map(indicatorDataCacheRef.current.get(indicatorIndex) ?? []);

      points.forEach((point) => {
        nextCache.set(keyForStructurePoint(point), point);
      });

      indicatorDataCacheRef.current.set(indicatorIndex, nextCache);
      return Array.from(nextCache.values()).sort(
        (left, right) =>
          sortValueForStructurePoint(left) - sortValueForStructurePoint(right)
      );
    },
    []
  );

  const drawIndicatorData = useCallback(
    (indicatorIndex: number, data: MarketStructureIndicatorUpdate['data']) => {
      const chart = chartRef.current;
      if (!chart) return;

      // MARKET_STRUCTURE can repaint prior events; rebuild series from cached merged state.
      clearStructureSeriesForIndicator(indicatorIndex);
      clearZigZagSeriesForIndicator(indicatorIndex);

      const newStructureSeries: ISeriesApi<'Line'>[] = [];

      data.forEach((item) => {
        const structureSeries = createStructureLineWithLabel(chart, item);
        if (!structureSeries) return;

        newStructureSeries.push(structureSeries);
      });

      structureSeriesRefs.current.set(indicatorIndex, newStructureSeries);

      if (!showZigZag) return;

      const zigZagData = data
        .flatMap((item) => {
          const time = item.zigZag?.swingEnd?.time;
          const value = item.zigZag?.swingEnd?.price;
          if (time == null || typeof value !== 'number') return [];

          return [{ time, value }];
        })
        .sort(
          (left, right) =>
            toSortableTime(left.time) - toSortableTime(right.time)
        );

      if (zigZagData.length === 0) return;

      const zigZagSeries = chart.addSeries(LineSeries, {
        color: 'rgba(255, 255, 255, 0.7)',
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false
      });

      zigZagSeries.setData(zigZagData);
      zigZagSeriesRefs.current.set(indicatorIndex, zigZagSeries);
    },
    [
      clearStructureSeriesForIndicator,
      clearZigZagSeriesForIndicator,
      createStructureLineWithLabel
    ]
  );

  const applyUpdate = useCallback(
    (update: MarketStructureIndicatorUpdate) => {
      const mergedData = mergeIndicatorData(
        update.indicatorIndex,
        update.data,
        update.updateType === 'snapshot'
      );

      // Rebuild full set because structure events can be revised at prior times.
      drawIndicatorData(update.indicatorIndex, mergedData);
    },
    [drawIndicatorData, mergeIndicatorData]
  );

  useEffect(() => {
    if (hasMarketStructure) return;

    indicatorDataCacheRef.current.clear();
    clearAll();
  }, [clearAll, hasMarketStructure]);

  useEffect(() => {
    // Timeframe switch clears stale cache/series from previous dataset.
    indicatorDataCacheRef.current.clear();
    clearAll();
  }, [clearAll, timeframeIndex]);

  useIndicatorUpdateStream({
    chartInitialized,
    enabled: hasMarketStructure,
    indicatorType: IndicatorType.MARKET_STRUCTURE,
    isExpectedUpdate: isMarketStructureIndicatorUpdate,
    applyUpdate
  });

  useEffect(() => {
    return () => {
      indicatorDataCacheRef.current.clear();
      clearAll();
    };
  }, [clearAll]);
};
